import { useEffect } from 'react';
import {
  useCart,
  useCartTotals,
  useSettings,
  useProductByHandle,
  useCartAddItem,
  useCartRemoveItem,
  useCountry,
  useCollectionByHandle,
} from '@backpackjs/storefront';
import Cookies from 'js-cookie';
import { getSizedImageUrl } from '../../utilities/Images';
import { IconRemove } from '..';

export function GiftWithPurchase({ item }) {
  const cart = useCart();
  const cartTotals = useCartTotals();
  const siteSettings = useSettings();
  const { country } = useCountry();
  const countryCode = country?.isoCode || 'US';
  const isUs = countryCode === 'US';
  const { cartAddItem } = useCartAddItem();
  const { cartRemoveItem } = useCartRemoveItem();
  const optedOut = Cookies.get('gwp_opted-out') === 'true';

  const giftType = siteSettings?.freeProducts?.giftType || 'disabled';

  const {
    gwpProduct,
    gwpCartMinimum,
    gwpAddBack,
    gwpProductQualify,
    gwpCollectionQualify,
    enableSubQualify,
    gwpAllSubsQualify,
    gwpBundleCount,
  } = {
    ...siteSettings?.freeProducts?.[`${giftType}Settings`],
  };

  const { product } = useProductByHandle({
    handle: gwpProduct?.handle || 'gwpProductHandle',
  });
  const cartTotal = parseFloat(cartTotals?.total);

  const { collection } = useCollectionByHandle({
    handle: gwpCollectionQualify || 'gwpCollectionQualify',
  });
  const productsHandles = (collection?.products || []).map((p) => p?.handle);

  const qualifiedLine = cart?.lines.find((line) => {
    if (giftType === 'buyxgety') {
      if (enableSubQualify) {
        return (
          line?.variant?.product?.handle === gwpProductQualify?.handle &&
          !!line?.sellingPlanAllocation?.sellingPlan
        );
      }
      return line?.variant?.product?.handle === gwpProductQualify?.handle;
    }
    if (giftType === 'buyfromcollections') {
      return productsHandles.includes(line?.variant?.product?.handle);
    }
    if (giftType === 'buysubgety' && gwpAllSubsQualify) {
      return !!line?.sellingPlanAllocation?.sellingPlan;
    }
    if (giftType === 'buybundlegety') {
      const countAttribute = line.attributes.find(
        (attribute) => attribute.key === '_bundleCount'
      );

      return (
        Number.parseInt(gwpBundleCount, 10) <=
        Number.parseInt(countAttribute?.value, 10)
      );
    }
    return false;
  });

  const gwpLine = cart?.lines.find(
    (line) => product && line?.variant?.product?.handle === product?.handle
  );
  const gwpInCart = !!gwpLine;

  const addGwp = async () => {
    if (product?.variants?.[0]?.id) {
      await cartAddItem({
        merchandiseId: product?.variants?.[0]?.id,
        quantity: 1,
        attributes: [{ key: '_gwp', value: 'true' }],
      });
    }
  };

  const removeGwp = async () => {
    if (gwpLine?.id) {
      await cartRemoveItem({ lineId: gwpLine?.id });
    }
  };

  const qualifiedFreeGift = () => {
    if (
      giftType === 'gwp' &&
      gwpCartMinimum &&
      cartTotal >= gwpCartMinimum &&
      isUs
    ) {
      return true;
    }
    if (giftType === 'buyxgety' && !!qualifiedLine && isUs) {
      return true;
    }
    if (giftType === 'buyfromcollections' && !!qualifiedLine && isUs) {
      return true;
    }
    if (giftType === 'buysubgety' && !!qualifiedLine && isUs) {
      return true;
    }

    if (giftType === 'buybundlegety' && !!qualifiedLine && isUs) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (qualifiedFreeGift() && !gwpInCart && !optedOut) {
      addGwp();
    }
    if (!qualifiedFreeGift()) {
      removeGwp();
    }
  }, [cartTotal, cart?.lines?.length, isUs, qualifiedFreeGift()]);

  if (
    !gwpProduct ||
    !product ||
    !isUs ||
    (giftType === 'gwp' && (!gwpCartMinimum || cartTotal < gwpCartMinimum)) ||
    (giftType === 'buyxgety' && !qualifiedLine) ||
    (giftType === 'buyfromcollections' && !qualifiedLine) ||
    (giftType === 'buysubgety' && !qualifiedLine) ||
    (giftType === 'buybundlegety' && !qualifiedLine)
  ) {
    return null;
  }

  if (!gwpInCart && optedOut) {
    return (
      <div className="line-item-add-gwp relative flex items-center justify-between border-b border-grey-4 py-5 px-4">
        <button
          type="button"
          className="border-b-2 font-gothamBold text-sm"
          onClick={() => {
            Cookies.remove('gwp_opted-out');
            addGwp();
          }}
        >
          {gwpAddBack}
        </button>
      </div>
    );
  }

  if (!gwpLine) return null;

  return (
    <div className="gwp-line-item-container relative flex items-center justify-between border-b border-grey-4 py-5 px-4">
      <div className="w-20 shrink-0">
        <img
          src={getSizedImageUrl(gwpLine?.variant?.image?.src, '100x100@2x')}
          alt={gwpLine?.variant?.product?.title}
        />
      </div>
      <button
        className="z-1 absolute right-4 top-5 inline-block cursor-pointer"
        onClick={() => {
          removeGwp();
          Cookies.set('gwp_opted-out', true);
        }}
        type="button"
      >
        <IconRemove />
      </button>
      <div className="relative flex-grow pl-3">
        <div className="my-0 inline-block pr-8 font-gothamBold text-xs no-underline">
          {gwpLine?.variant?.product?.title}
        </div>
        <p className="mt-0 mb-0 text-xs lg:mt-1">one-time purchase</p>
        <div className="mt-6 flex items-center justify-between">
          <span />
          <div className="text-right font-gothamBold text-sm">FREE</div>
        </div>
      </div>
    </div>
  );
}

export default GiftWithPurchase;
